import React, { useState } from 'react';
import api from '../../utils/api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly (3 months)' }
];

const emptyBillingPlan = () => ({ name: '', frequency: 'monthly', amount: '', currency: 'USD', autoRenew: true, refundPolicy: 'none' });

const defaultBillingPlans = (existing) => {
  if (existing?.length > 0) return existing.map(p => ({ ...p }));
  return [
    { name: 'Monthly', frequency: 'monthly', amount: '', currency: 'USD', autoRenew: true, refundPolicy: 'none' },
    { name: 'Quarterly', frequency: 'quarterly', amount: '', currency: 'USD', autoRenew: true, refundPolicy: 'none' }
  ];
};

const CourseForm = ({ studio, course, onSaved, onCancel }) => {
  const isEdit = !!course;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: course?.name || '',
    description: course?.description || '',
    danceStyle: course?.danceStyle || (studio?.danceStyles?.[0] || ''),
    levelName: course?.levelName || '',
    type: course?.type || 'long_course',
    schedule: {
      days: course?.schedule?.days || [],
      startTime: course?.schedule?.startTime || '',
      endTime: course?.schedule?.endTime || '',
      timezone: course?.schedule?.timezone || 'America/Chicago',
      sessions: course?.schedule?.sessions || [],
      // Block course fields
      blockStartDate: course?.schedule?.blockStartDate ? new Date(course.schedule.blockStartDate).toISOString().split('T')[0] : '',
      blockWeeks: course?.schedule?.blockWeeks || 8,
      // Workshop fields
      sessionDate: course?.schedule?.sessionDate ? new Date(course.schedule.sessionDate).toISOString().split('T')[0] : '',
      sessionStartTime: course?.schedule?.sessionStartTime || '',
      sessionEndTime: course?.schedule?.sessionEndTime || ''
    },
    location: {
      type: course?.location?.type || 'in_person',
      address: course?.location?.address || '',
      meetLink: course?.location?.meetLink || ''
    },
    capacity: course?.capacity || 20,
    billingPlans: defaultBillingPlans(course?.billingPlans),
    requireAdvanceDeposit: course?.requireAdvanceDeposit !== false,
    materialReleaseDelay: course?.materialReleaseDelay || '2_days',
    status: course?.status || 'active'
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateNested = (parent, field, value) => {
    setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        days: prev.schedule.days.includes(day)
          ? prev.schedule.days.filter(d => d !== day)
          : [...prev.schedule.days, day]
      }
    }));
  };

  const updateBillingPlan = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      billingPlans: prev.billingPlans.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const addBillingPlan = () => {
    setForm(prev => ({ ...prev, billingPlans: [...prev.billingPlans, emptyBillingPlan()] }));
  };

  const removeBillingPlan = (index) => {
    setForm(prev => ({ ...prev, billingPlans: prev.billingPlans.filter((_, i) => i !== index) }));
  };

  // Block course sessions
  const addSession = () => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: [...prev.schedule.sessions, { date: '', startTime: '', endTime: '' }]
      }
    }));
  };

  const updateSession = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: prev.schedule.sessions.map((s, i) => i === index ? { ...s, [field]: value } : s)
      }
    }));
  };

  const removeSession = (index) => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: prev.schedule.sessions.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        billingPlans: form.billingPlans
          .filter(p => p.amount)
          .map(p => ({
            ...p,
            name: p.name || (p.frequency === 'monthly' ? 'Monthly' : 'Quarterly'),
            amount: parseFloat(p.amount)
          })),
        requireAdvanceDeposit: form.requireAdvanceDeposit,
        capacity: parseInt(form.capacity) || 20
      };

      // Clean schedule based on type
      if (form.type === 'long_course') {
        delete payload.schedule.sessions;
        delete payload.schedule.sessionDate;
        delete payload.schedule.sessionStartTime;
        delete payload.schedule.sessionEndTime;
        delete payload.schedule.blockStartDate;
        delete payload.schedule.blockWeeks;
      } else if (form.type === 'block') {
        // Block uses: blockStartDate, blockWeeks, days, startTime, endTime, timezone
        delete payload.schedule.sessions;
        delete payload.schedule.sessionDate;
        delete payload.schedule.sessionStartTime;
        delete payload.schedule.sessionEndTime;
      } else if (form.type === 'workshop') {
        delete payload.schedule.days;
        delete payload.schedule.startTime;
        delete payload.schedule.endTime;
        delete payload.schedule.sessions;
        delete payload.schedule.blockStartDate;
        delete payload.schedule.blockWeeks;
      }

      let result;
      if (isEdit) {
        result = await api.put(`/courses/${course._id}`, payload);
      } else {
        result = await api.post('/courses', payload);
      }
      onSaved(result.data.course);
    } catch (err) {
      setError(err.response?.data?.details?.join(', ') || err.response?.data?.error || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const canProceedStep1 = form.name && form.type;
  const canProceedStep2 = () => {
    if (form.type === 'long_course') return form.schedule.days.length > 0;
    if (form.type === 'block') return form.schedule.blockStartDate && form.schedule.days.length > 0 && form.schedule.blockWeeks > 0;
    if (form.type === 'workshop') return form.schedule.sessionDate;
    return true;
  };
  const canSubmit = form.billingPlans.some(p => p.amount);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Course' : 'Create Course'}
        </h1>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 text-sm font-medium">
          Cancel
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center sm:justify-start gap-1 sm:gap-2 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex items-center gap-1 sm:gap-2">
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
              s <= step ? 'bg-brand-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {s}
            </div>
            <span className={`text-xs sm:text-sm ${s <= step ? 'text-gray-900' : 'text-gray-400'}`}>
              {s === 1 ? 'Basics' : s === 2 ? 'Schedule' : 'Pricing'}
            </span>
            {s < 3 && <div className="w-6 sm:w-12 h-px bg-gray-300 mx-0.5 sm:mx-1" />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">

        {/* Step 1: Basics */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                placeholder="e.g., Bharatanatyam Level 2 - Fall 2026"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => update('description', e.target.value)}
                placeholder="What will students learn in this course?"
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course Type *</label>
                <select
                  value={form.type}
                  onChange={e => update('type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                >
                  <option value="long_course">Long Course (ongoing)</option>
                  <option value="block">Block (fixed sessions)</option>
                  <option value="workshop">Workshop (one-time)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  value={form.location.type}
                  onChange={e => updateNested('location', 'type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                >
                  <option value="in_person">In-Person</option>
                  <option value="online">Online</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            {(form.location.type === 'in_person' || form.location.type === 'hybrid') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={form.location.address}
                  onChange={e => updateNested('location', 'address', e.target.value)}
                  placeholder="Studio address"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            )}

            {(form.location.type === 'online' || form.location.type === 'hybrid') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Link</label>
                <input
                  type="url"
                  value={form.location.meetLink}
                  onChange={e => updateNested('location', 'meetLink', e.target.value)}
                  placeholder="https://zoom.us/j/..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dance Style</label>
                {studio?.danceStyles?.length > 0 ? (
                  <select
                    value={form.danceStyle}
                    onChange={e => update('danceStyle', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {studio.danceStyles.map(s => (
                      <option key={s} value={s} className="capitalize">{s}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.danceStyle}
                    onChange={e => update('danceStyle', e.target.value)}
                    placeholder="e.g., Bharatanatyam"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                {studio?.levels?.length > 0 ? (
                  <select
                    value={form.levelName}
                    onChange={e => update('levelName', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {studio.levels
                      .sort((a, b) => a.order - b.order)
                      .map(l => (
                        <option key={l.name} value={l.name}>{l.name}</option>
                      ))
                    }
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.levelName}
                    onChange={e => update('levelName', e.target.value)}
                    placeholder="e.g., Stage 2"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                )}
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Next: Schedule
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Schedule */}
        {step === 2 && (
          <div className="space-y-4">
            {form.type === 'long_course' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Class Days *</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(day => (
                      <button
                        key={day}
                        onClick={() => toggleDay(day)}
                        className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                          form.schedule.days.includes(day)
                            ? 'bg-brand-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={form.schedule.startTime}
                      onChange={e => updateNested('schedule', 'startTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={form.schedule.endTime}
                      onChange={e => updateNested('schedule', 'endTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                    <select
                      value={form.schedule.timezone}
                      onChange={e => updateNested('schedule', 'timezone', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    >
                      <option value="America/New_York">Eastern (ET)</option>
                      <option value="America/Chicago">Central (CT)</option>
                      <option value="America/Denver">Mountain (MT)</option>
                      <option value="America/Los_Angeles">Pacific (PT)</option>
                      <option value="Asia/Kolkata">India (IST)</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {form.type === 'block' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={form.schedule.blockStartDate}
                      onChange={e => updateNested('schedule', 'blockStartDate', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (weeks) *</label>
                    <input
                      type="number"
                      value={form.schedule.blockWeeks}
                      onChange={e => updateNested('schedule', 'blockWeeks', parseInt(e.target.value) || '')}
                      min="1"
                      max="52"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Class Days *</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(day => (
                      <button
                        key={day}
                        onClick={() => toggleDay(day)}
                        className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                          form.schedule.days.includes(day)
                            ? 'bg-brand-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={form.schedule.startTime}
                      onChange={e => updateNested('schedule', 'startTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={form.schedule.endTime}
                      onChange={e => updateNested('schedule', 'endTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                    <select
                      value={form.schedule.timezone}
                      onChange={e => updateNested('schedule', 'timezone', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    >
                      <option value="America/New_York">Eastern (ET)</option>
                      <option value="America/Chicago">Central (CT)</option>
                      <option value="America/Denver">Mountain (MT)</option>
                      <option value="America/Los_Angeles">Pacific (PT)</option>
                      <option value="Asia/Kolkata">India (IST)</option>
                    </select>
                  </div>
                </div>
                {form.schedule.blockStartDate && form.schedule.days.length > 0 && form.schedule.blockWeeks > 0 && (
                  <p className="text-xs text-gray-500">
                    {form.schedule.blockWeeks} weeks &middot; {form.schedule.days.length} day{form.schedule.days.length > 1 ? 's' : ''}/week &middot; {form.schedule.blockWeeks * form.schedule.days.length} total sessions
                  </p>
                )}
              </>
            )}

            {form.type === 'workshop' && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.schedule.sessionDate}
                    onChange={e => updateNested('schedule', 'sessionDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={form.schedule.sessionStartTime}
                    onChange={e => updateNested('schedule', 'sessionStartTime', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={form.schedule.sessionEndTime}
                    onChange={e => updateNested('schedule', 'sessionEndTime', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Material Release Delay</label>
              <select
                value={form.materialReleaseDelay}
                onChange={e => update('materialReleaseDelay', e.target.value)}
                className="w-48 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              >
                <option value="immediately">Immediately after class</option>
                <option value="1_day">1 day after class</option>
                <option value="2_days">2 days after class</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">When students unlock tutorial videos after attending a class.</p>
            </div>

            <div className="pt-4 flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="text-gray-600 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2()}
                className="bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Next: Pricing
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">Pricing *</label>
              <p className="text-xs text-gray-500 mb-4">
                Set your price for each billing cycle. Students choose their preferred plan at checkout. You keep 100% of the price you set.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {form.billingPlans.map((plan, i) => {
                  const amt = parseFloat(plan.amount) || 0;
                  const studentPrice = amt > 0 ? Math.ceil(amt * 1.06) : 0;
                  return (
                    <div key={plan.frequency} className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        {plan.frequency === 'monthly' ? 'Monthly' : 'Quarterly (3 months)'}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          value={plan.amount}
                          onChange={e => updateBillingPlan(i, 'amount', e.target.value)}
                          placeholder={plan.frequency === 'monthly' ? '100' : '270'}
                          min="0"
                          step="1"
                          className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                        />
                      </div>
                      {studentPrice > 0 && (
                        <p className="text-xs text-gray-500 mt-1.5">
                          Student pays <span className="font-semibold text-gray-700">${studentPrice}</span>
                          {plan.frequency === 'monthly' ? '/mo' : '/quarter'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Advance Deposit Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="advanceDeposit"
                checked={form.requireAdvanceDeposit}
                onChange={e => update('requireAdvanceDeposit', e.target.checked)}
                className="mt-1 accent-brand-500 w-4 h-4"
              />
              <label htmlFor="advanceDeposit" className="text-sm cursor-pointer">
                <span className="font-medium text-gray-700">Require advance deposit (1 month)</span>
                <span className="block text-xs text-gray-500">Collected at enrollment, applied to student's last month when they give notice</span>
              </label>
            </div>

            {/* Pricing Preview */}
            {form.billingPlans.some(p => p.amount) && (
              <div className="bg-brand-50 border border-brand-100 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Student pricing preview</p>
                <div className="space-y-1.5">
                  {form.billingPlans.filter(p => p.amount).map((plan, i) => {
                    const amt = parseFloat(plan.amount) || 0;
                    const studentPrice = Math.ceil(amt * 1.06);
                    const monthlyPlan = form.billingPlans.find(p => p.frequency === 'monthly' && parseFloat(p.amount) > 0);
                    const monthlyAmt = parseFloat(monthlyPlan?.amount) || amt;
                    const hasAdvance = form.requireAdvanceDeposit;
                    const enrollmentTotal = hasAdvance ? Math.ceil((amt + monthlyAmt) * 1.06) : studentPrice;
                    const advanceStudentPrice = hasAdvance ? Math.ceil(monthlyAmt * 1.06) : 0;
                    const freq = plan.frequency === 'monthly' ? '/mo' : '/quarter';

                    return (
                      <div key={i} className="text-sm">
                        <span className="font-medium text-gray-800">{plan.frequency === 'monthly' ? 'Monthly' : 'Quarterly'}</span>
                        {hasAdvance ? (
                          <span className="text-gray-600">
                            {' '}&mdash; Enrollment: <span className="font-semibold text-gray-900">${enrollmentTotal}</span>
                            <span className="text-xs text-gray-500"> (incl. ${advanceStudentPrice} advance deposit)</span>
                            {' '}&middot; Then <span className="font-semibold text-gray-900">${studentPrice}{freq}</span>
                          </span>
                        ) : (
                          <span className="text-gray-600">
                            {' '}&mdash; <span className="font-semibold text-gray-900">${studentPrice}{freq}</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}


            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="pt-4 flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="text-gray-600 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !canSubmit}
                className="bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
              >
                {saving ? 'Saving...' : isEdit ? 'Update Course' : 'Create Course'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseForm;
